<template>
  <div id="app">
    <aside class="sidebar">
      <button class="brand" @click="go('create')" aria-label="进入创作台">
        <span class="brand-mark">A</span><strong>ARC<span>AI</span></strong>
      </button>
      <nav>
        <button :class="{ active: page === 'create' }" @click="go('create')"><span class="nav-icon">✦</span>创作台</button>
        <button :class="{ active: page === 'gallery' }" @click="go('gallery')"><span class="nav-icon">▦</span>我的作品</button>
        <button :class="{ active: page === 'pricing' }" @click="go('pricing')"><span class="nav-icon">◇</span>价格方案</button>
      </nav>
      <div class="sidebar-bottom">
        <button class="credits" @click="go('pricing')">
          <span><small>可用算力</small><strong>{{ user ? user.money || user.score || 0 : '--' }}</strong></span>
          <span class="plus">+</span>
        </button>
        <div v-if="user" class="account">
          <div class="avatar">{{ displayName.slice(0, 1).toUpperCase() }}</div>
          <span><strong>{{ displayName }}</strong><small>{{ user.email || user.mobile || '创作者' }}</small></span>
          <button class="icon-button" title="退出登录" @click="logout">↗</button>
        </div>
        <button v-else class="login-entry" @click="openAuth('login')">登录 / 注册</button>
      </div>
    </aside>

    <main>
      <header class="topbar">
        <div>
          <small>{{ page === 'create' ? 'AI IMAGE STUDIO' : page === 'gallery' ? 'MY LIBRARY' : 'MEMBERSHIP' }}</small>
          <h1>{{ pageTitle }}</h1>
        </div>
        <div class="header-actions">
          <span class="status"><i></i>服务正常</span>
          <button v-if="!user" class="button ghost" @click="openAuth('login')">登录</button>
          <button v-if="!user" class="button primary" @click="openAuth('register')">免费注册</button>
        </div>
      </header>
      <div class="mobile-desktop-notice" role="note">
        <span>为了获得更好的创作体验，建议使用电脑访问并使用</span>
      </div>

      <section v-if="page === 'create'" class="workspace">
        <div class="control-panel">
          <div class="panel-title"><span>{{ form.mode === 'edit' ? '编辑设置' : form.mode === 'video' ? '视频设置' : '生成设置' }}</span><small>本次消耗 {{ currentCostText }} 算力</small></div>
          <div class="mode-switch" role="tablist" aria-label="创作模式">
            <button v-for="mode in modes" :key="mode.value" :class="{ selected: form.mode === mode.value }" role="tab" :aria-selected="form.mode === mode.value" @click="setMode(mode.value)">{{ mode.label }}</button>
          </div>
          <div v-if="form.mode === 'video'" class="mode-switch sub-switch" role="tablist" aria-label="视频生成方式">
            <button :class="{ selected: videoInputMode === 'single' }" role="tab" :aria-selected="videoInputMode === 'single'" @click="setVideoInputMode('single')">单段生成</button>
            <button :class="{ selected: videoInputMode === 'batch' }" role="tab" :aria-selected="videoInputMode === 'batch'" @click="setVideoInputMode('batch')">长文案分段</button>
          </div>
          <label v-if="form.mode === 'edit'" class="field upload-field">
            <span>参考图片 <em>JPG / PNG / WEBP，最大 10MB</em></span>
            <input ref="imageInput" class="file-input" type="file" accept="image/jpeg,image/png,image/webp" @change="selectImage" />
            <button v-if="!imagePreview" class="upload-box" type="button" @click="$refs.imageInput.click()"><b>＋</b><span>上传待编辑图片</span></button>
            <div v-else class="upload-preview"><img :src="imagePreview" alt="待编辑图片预览" /><div><span>{{ sourceImage.name }}</span><button type="button" @click="$refs.imageInput.click()">重新选择</button></div></div>
          </label>
          <label v-if="form.mode === 'video' && videoInputMode === 'single'" class="field upload-field">
            <span>参考图片（可选）<em>JPG / PNG / WEBP，最大 10MB</em></span>
            <input ref="videoImageInput" class="file-input" type="file" accept="image/jpeg,image/png,image/webp" @change="selectVideoImage" />
            <button v-if="!videoImagePreview" class="upload-box" type="button" @click="$refs.videoImageInput.click()"><b>＋</b><span>上传参考图（图生视频）</span></button>
            <div v-else class="upload-preview"><img :src="videoImagePreview" alt="参考图片预览" /><div><span>{{ videoImage.name }}</span><button type="button" @click="$refs.videoImageInput.click()">重新选择</button><button type="button" @click="clearVideoImage">移除</button></div></div>
            <em class="upload-hint">不上传则为纯文生视频</em>
          </label>
          <label v-if="form.mode !== 'video' || videoInputMode === 'single'" class="field">
            <span>创意描述 <em>{{ form.prompt.length }}/800</em></span>
            <textarea v-model.trim="form.prompt" maxlength="800" placeholder="描述你想创造的画面，例如：雨夜中的未来都市，霓虹灯倒映在街道上，电影感构图..."></textarea>
          </label>
          <div v-if="form.mode !== 'video' || videoInputMode === 'single'" class="prompt-actions">
            <button @click="randomPrompt">✦ 灵感</button>
            <button @click="form.prompt = ''">清空</button>
          </div>
          <div v-if="form.mode === 'video' && videoInputMode === 'batch'" class="batch-panel">
            <label class="field">
              <span>视频文案 <em>{{ scriptText.length }}/{{ maxScriptLength }}</em></span>
              <textarea v-model.trim="scriptText" :maxlength="maxScriptLength" :disabled="batchGenerating" class="script-input" placeholder="把整段视频文案一次性粘贴到这里，点击“智能拆分”会改写成分镜，再逐段生成视频"></textarea>
            </label>
            <div class="prompt-actions">
              <button :disabled="splitting || batchGenerating" @click="splitScript">{{ splitting ? '拆分中...' : '✦ 智能拆分' + (splitPrice > 0 ? ' · ' + formatCost(splitPrice) + ' 算力' : '') }}</button>
              <button :disabled="splitting || batchGenerating" @click="clearScript">清空</button>
            </div>
            <div class="character-panel">
              <div class="character-head">
                <span>角色设定 <em>智能拆分后自动填入，可增删修改；每个角色都能单独生成定妆照</em></span>
                <button type="button" class="character-add" :disabled="batchGenerating" @click="addCharacter">＋ 添加角色</button>
              </div>
              <input ref="characterPortraitInput" class="file-input" type="file" accept="image/jpeg,image/png,image/webp" @change="selectCharacterPortrait" />
              <div v-for="(role, index) in characters" :key="'role-' + index" class="character-card">
                <div class="character-row">
                  <input v-model.trim="role.name" maxlength="12" :disabled="batchGenerating" placeholder="角色名，如：女主" />
                  <textarea v-model.trim="role.desc" maxlength="120" :disabled="batchGenerating" placeholder="外貌：年龄、性别、脸型、发型发色、体型、服装"></textarea>
                  <button type="button" :disabled="batchGenerating || characters.length <= 1" title="删除该角色" @click="removeCharacter(index)">×</button>
                </div>
                <div class="character-foot">
                  <div class="character-thumb" :class="{ empty: !role.portraitPreview }"><img v-if="role.portraitPreview" :src="role.portraitPreview" alt="角色定妆照" /><span v-else>未设</span></div>
                  <button type="button" :disabled="batchGenerating || portraitBusyIndex !== -1" title="上传的照片会自动压成 1K JPEG（与 AI 定妆照同规格），避免上游忽略参考图" @click="pickCharacterPortrait(index)">{{ role.portraitPreview ? '更换定妆照' : '上传定妆照' }}</button>
                  <button type="button" :disabled="batchGenerating || portraitBusyIndex !== -1 || !(role.desc || '').trim()" @click="generateCharacterPortrait(index)"><span v-if="portraitBusyIndex === index" class="spinner"></span><span v-else>✦</span>{{ portraitBusyIndex === index ? '生成中...' : 'AI 生成定妆照' }}<em v-if="portraitPrice > 0 && portraitBusyIndex !== index"> · {{ formatCost(portraitPrice) }} 算力</em></button>
                  <button v-if="role.portraitPreview" type="button" :disabled="batchGenerating || portraitBusyIndex !== -1" @click="clearCharacterPortrait(index)">移除</button>
                </div>
              </div>
              <div v-if="!characters.length" class="character-empty">还没有角色设定，点击「＋ 添加角色」，或先粘贴文案做智能拆分</div>
              <div class="character-hint">起手图规则：第 1 段与「新场景」用该段出场角色的定妆照；「承接上一段」用上一段最后一帧（可同时锁住配角的相貌与动作）</div>
              <button class="strict-toggle" type="button" :class="{ selected: strictPortrait }" :disabled="batchGenerating || !hasCharacterPortrait" @click="strictPortrait = !strictPortrait">
                <b>{{ strictPortrait ? '✓' : '' }}</b>
                <span>强约束模式 · 每段都用角色定妆照起手<em>{{ hasCharacterPortrait ? '角色长相最稳，代价是每段开头构图都被定妆照约束、段间动作无法承接；关闭则承接上一段最后一帧' : '需要先给角色上传或用 AI 生成至少一张定妆照' }}</em></span>
              </button>
            </div>
            <div v-if="scenes.length" class="scene-list">
              <div class="scene-list-head"><span>分镜列表 · {{ scenes.length }} 段</span><small>「承接上一段」用上一段最后一帧起手，「新场景」用该段角色的定妆照起手</small></div>
              <div v-for="(scene, index) in scenes" :key="index" class="scene-item">
                <div class="scene-item-head">
                  <b>第 {{ index + 1 }} 段</b>
                  <input v-model.trim="scene.title" maxlength="30" :disabled="batchGenerating" placeholder="分镜标题" />
                  <button type="button" :disabled="batchGenerating" title="删除本段" @click="removeScene(index)">×</button>
                </div>
                <input v-model.trim="scene.scene" class="scene-input" maxlength="100" :disabled="batchGenerating" placeholder="环境设定（地点/时间/光线与色调），会拼到提示词前" />
                <textarea v-model.trim="scene.prompt" maxlength="1500" :disabled="batchGenerating" placeholder="该镜头的画面提示词"></textarea>
                <div class="scene-item-foot">
                  <button type="button" :class="{ selected: scene.transition !== 'cut' }" :disabled="batchGenerating || index === 0" @click="scene.transition = 'continue'">承接上一段</button>
                  <button type="button" :class="{ selected: scene.transition === 'cut' }" :disabled="batchGenerating" @click="scene.transition = 'cut'">新场景</button>
                  <em class="scene-cast">出场：{{ sceneCastText(scene) }}</em>
                  <img v-if="startThumb(index, scene)" class="scene-start-thumb" :src="startThumb(index, scene)" alt="起手图预览" title="本段实际用于起手的参考图" />
                  <em class="scene-start">{{ startHint(index, scene) }}</em>
                </div>
              </div>
            </div>
          </div>
          <div v-if="form.mode !== 'video'" class="field">
            <span>清晰度 <em>单张价格</em></span>
            <div class="quality-grid">
              <button v-for="quality in qualities" :key="quality.value" :class="{ selected: form.quality === quality.value }" @click="form.quality = quality.value"><b>{{ quality.label }}</b><span>{{ formatCost(quality.price) }} 算力</span><small>{{ quality.description }}</small></button>
            </div>
          </div>
          <div v-if="form.mode !== 'video'" class="field">
            <span>画面比例</span>
            <div class="ratio-grid">
              <button v-for="ratio in ratios" :key="ratio.value" :class="{ selected: form.ratio === ratio.value }" @click="form.ratio = ratio.value">
                <i :style="ratio.style"></i><b>{{ ratio.label }}</b><small>{{ selectedSize(ratio.value) }}</small>
              </button>
            </div>
          </div>
          <label v-if="form.mode === 'video'" class="field range-field">
            <span>每段时长 <b>{{ form.videoDuration }} 秒</b></span>
            <input v-model.number="form.videoDuration" type="range" :min="videoMinDuration" :max="videoMaxDuration" step="1" />
            <div class="range-ticks"><span>{{ videoMinDuration }}</span><span>8</span><span>{{ videoMaxDuration }}</span></div>
          </label>
          <div v-if="form.mode === 'video'" class="field">
            <span>视频清晰度 <em>每秒单价</em></span>
            <div class="quality-grid">
              <button v-for="res in videoResolutions" :key="res.value" :class="{ selected: form.videoResolution === res.value }" @click="form.videoResolution = res.value"><b>{{ res.label }}</b><span>{{ formatCost(res.price) }} 算力/秒</span></button>
            </div>
          </div>
          <div class="cost-summary">
            <span v-if="form.mode === 'video' && videoInputMode === 'batch'">{{ scenes.length }} 段 × {{ form.videoDuration }} 秒 · {{ selectedVideoResolution.label }}</span>
            <span v-else-if="form.mode === 'video'">{{ selectedVideoResolution.label }} × {{ form.videoDuration }} 秒</span>
            <span v-else>{{ selectedQuality.label }} · 单张</span>
            <strong>{{ currentCostText }} 算力</strong>
          </div>
          <button v-if="form.mode !== 'video' || videoInputMode === 'single'" class="generate-button" :disabled="generating" @click="form.mode === 'video' ? generateVideo() : generate()">
            <span v-if="generating" class="spinner"></span><span v-else>✦</span>{{ generateButtonText }}
          </button>
          <template v-else>
            <button class="generate-button" :disabled="batchGenerating || !scenes.length || batchRemaining === 0" @click="startBatch">
              <span v-if="batchGenerating" class="spinner"></span><span v-else>✦</span>{{ batchButtonText }}
            </button>
            <button v-if="batchGenerating" class="button ghost batch-stop" @click="stopBatch">停止后续生成</button>
          </template>
          <div v-if="generateError" class="generate-error" role="alert">{{ generateError }}</div>
        </div>

        <div class="canvas-panel">
          <template v-if="form.mode === 'video'">
            <div v-if="!activeVideoTasks.length && !generating && !batchGenerating" class="empty-state">
              <div class="empty-visual"><span>✦</span><i></i><b></b></div>
              <h2>{{ videoInputMode === 'batch' ? '长文案一键分段生成' : '生成你的第一段视频' }}</h2>
              <p>{{ videoInputMode === 'batch' ? '在左侧粘贴整段视频文案，智能拆分成分镜后逐段生成，每段成功才扣费。' : '输入画面描述，设置时长与清晰度，AI 将在这里呈现你的视频作品。' }}</p>
              <div class="examples"><button v-for="item in promptExamples" :key="item.title" @click="usePrompt(item.prompt)"><span>{{ item.icon }}</span><b>{{ item.title }}</b><small>{{ item.desc }}</small></button></div>
            </div>
            <div v-else-if="!activeVideoTasks.length" class="generating-state"><div class="scan-box"><span>✦</span></div><h2>{{ videoInputMode === 'batch' ? '正在分段生成' : '正在提交视频任务' }}</h2><p>请稍候，正在逐段创建生成任务</p></div>
            <div v-else class="result-area video-result-area">
              <div class="result-head">
                <div><small>生成结果</small><h2>{{ videoInputMode === 'batch' ? `${batchDoneCount}/${scenes.length} 段已完成` : `${videoTasks.length} 个视频任务` }}</h2></div>
                <div v-if="videoInputMode === 'batch' && batchDoneCount" class="batch-download-actions">
                  <button class="button ghost" @click="downloadBatchZip">⬇ 分段打包 (ZIP)</button>
                  <button class="button ghost" :disabled="!mergeAvailable" :title="mergeAvailable ? '把所有片段拼接成一个完整视频' : '服务器未安装 ffmpeg，暂不支持拼接，请用分段打包'" @click="downloadBatchMerge">⬇ 拼接为一个视频 (MP4)</button>
                </div>
              </div>
              <div class="video-task-list">
                <article v-for="task in activeVideoTasks" :key="task._batch ? 'seg-' + task._index : task.id" class="video-task">
                  <div class="video-task-head">
                    <div><strong>{{ (task.resolution || '').toUpperCase() }} · {{ task.duration }} 秒</strong><small v-if="task.segment_index">第 {{ task.segment_index }} 段 · {{ task.title || '未命名' }}</small><small v-else>{{ task.task_no }}</small></div>
                    <span class="video-tag" :class="task.status">{{ videoStatusText(task.status) }}</span>
                  </div>
                  <p class="video-task-prompt">{{ task.prompt }}</p>
                  <div v-if="task.status === 'processing'" class="video-progress">
                    <div class="video-progress-bar"><i :style="{ width: Math.min(100, Math.max(0, Number(task.progress) || 0)) + '%' }"></i></div>
                    <div class="video-progress-meta"><span>正在生成视频，请稍候</span><b>{{ Number(task.progress) || 0 }}%</b></div>
                  </div>
                  <div v-else-if="task.status === 'success'" class="video-player">
                    <video :src="assetUrl(task.url)" controls playsinline preload="metadata"></video>
                    <div class="video-actions"><button class="button ghost" @click="videoDownload(task)">下载</button><button class="button ghost" @click="openVideo(task)">新窗口打开</button><button v-if="task._batch" class="button ghost" :disabled="batchGenerating" @click="retryBatchSegment(task._index)">重新生成</button></div>
                  </div>
                  <div v-else class="video-failed">
                    <p>{{ task.error || '视频生成失败' }}</p>
                    <button v-if="task._batch" class="button ghost" :disabled="batchGenerating" @click="retryBatchSegment(task._index)">重试本段</button>
                    <small v-else>请修改描述或参考图后重试</small>
                  </div>
                  <div class="video-task-foot"><small>{{ formatCost(task.cost) }} 算力<template v-if="task.createtime_text"> · {{ task.createtime_text }}</template></small><img v-if="task.source_image" class="video-ref-thumb" :src="assetUrl(task.source_image)" alt="本段参考图" title="本段实际使用的起手参考图，可与视频第一帧对比" /><small v-if="task.id" class="video-ref" :class="{ warn: !task.ref }">{{ task.ref ? refText(task.ref) : '无参考图 · 角色会变脸' }}</small></div>
                </article>
              </div>
            </div>
          </template>
          <template v-else>
            <div v-if="!results.length && !generating" class="empty-state">
              <div class="empty-visual"><span>✦</span><i></i><b></b></div>
              <h2>让想象成为作品</h2>
              <p>输入画面描述并设置参数，AI 将在这里呈现你的创意。</p>
              <div class="examples"><button v-for="item in promptExamples" :key="item.title" @click="usePrompt(item.prompt)"><span>{{ item.icon }}</span><b>{{ item.title }}</b><small>{{ item.desc }}</small></button></div>
            </div>
            <div v-else-if="generating" class="generating-state"><div class="scan-box"><span>✦</span></div><h2>正在构筑画面</h2><p>AI 正在理解你的创意，请稍候</p></div>
            <div v-else class="result-area">
              <div class="result-head"><div><small>生成结果</small><h2>{{ results.length }} 张作品已完成</h2></div><button class="button ghost" @click="results = []">新建创作</button></div>
              <div class="result-grid" :class="`count-${results.length}`">
                <article v-for="(item, index) in results" :key="item.id || item.url || index">
                  <img :src="assetUrl(item.url || item.image, results.length === 1 ? 1200 : 800)" :alt="form.prompt" />
                  <div class="image-actions"><button title="下载作品" @click="download(item)">↓</button><button title="在新窗口查看" @click="preview(item)">↗</button></div>
                </article>
              </div>
            </div>
          </template>
        </div>
      </section>

      <section v-else-if="page === 'gallery'" class="content-section gallery-section">
        <div class="section-toolbar"><div><span class="eyebrow">创作档案</span><h2>我的作品</h2><p>浏览、下载和管理你生成的所有作品。</p></div><button class="button primary" @click="go('create')">✦ 开始创作</button></div>
        <div class="gallery-switch" role="tablist" aria-label="作品类型">
          <button :class="{ selected: galleryTab === 'image' }" role="tab" :aria-selected="galleryTab === 'image'" @click="switchGalleryTab('image')">图片</button>
          <button :class="{ selected: galleryTab === 'video' }" role="tab" :aria-selected="galleryTab === 'video'" @click="switchGalleryTab('video')">视频</button>
        </div>
        <template v-if="galleryTab === 'image'">
          <div v-if="galleryLoading" class="loading-list"><span class="spinner"></span>正在载入作品...</div>
          <div v-else-if="!works.length" class="gallery-empty"><span>▦</span><h3>还没有作品</h3><p>完成首次 AI 创作后，作品会自动保存在这里。</p><button class="button primary" @click="go('create')">创建第一张作品</button></div>
          <div v-else class="gallery-grid">
            <article v-for="work in works" :key="work.id">
              <div class="work-image"><img :src="assetUrl(work.url || work.image, 480)" :alt="work.prompt || 'AI 作品'" /><div><button @click="download(work)" title="下载">↓</button><button @click="removeWork(work)" title="删除">×</button></div></div>
              <p>{{ work.prompt || '未命名创作' }}</p><small>{{ work.createtime_text || work.createtime || '' }} · {{ work.model || 'AI' }}</small>
            </article>
          </div>
          <div v-if="galleryTotal > pageSize" class="pagination"><button :disabled="galleryPage === 1" @click="changeGalleryPage(-1)">‹</button><button v-for="n in totalPages" :key="n" :class="{ active: galleryPage === n }" @click="setGalleryPage(n)">{{ n }}</button><button :disabled="galleryPage === totalPages" @click="changeGalleryPage(1)">›</button></div>
        </template>
        <template v-else>
          <div v-if="videoGalleryLoading" class="loading-list"><span class="spinner"></span>正在载入视频...</div>
          <div v-else-if="!videoGroups.length" class="gallery-empty"><span>▦</span><h3>还没有视频作品</h3><p>完成首次视频生成后，作品会自动保存在这里。</p><button class="button primary" @click="startCreateVideo">生成第一段视频</button></div>
          <div v-else class="video-gallery-grid">
            <article v-for="group in videoGroups" :key="group.key" :class="{ 'video-group': isBatchGroup(group) }">
              <template v-if="isBatchGroup(group)">
                <div class="video-group-head">
                  <div>
                    <strong>{{ group.title || '分段视频' }}</strong>
                    <small>{{ group.createtime_text || '' }} · 共 {{ group.segments }} 段 · {{ group.duration }} 秒 · {{ (group.resolution || '').toUpperCase() }}</small>
                  </div>
                  <div class="video-group-actions">
                    <button class="button ghost" :disabled="group.segments < 2 || !mergeAvailable" :title="mergeAvailable ? '把本组所有分段按顺序拼接成一个视频' : '服务器未安装 ffmpeg，暂不支持拼接'" @click="downloadGroupMerge(group)">⬇ 合并为一个视频</button>
                    <button class="button ghost" @click="downloadGroupZip(group)">⬇ 分段打包 (ZIP)</button>
                  </div>
                </div>
                <div class="video-group-segments">
                  <div v-for="task in group.list" :key="task.id" class="video-group-segment">
                    <video :src="assetUrl(task.url)" preload="metadata" muted playsinline></video>
                    <div class="video-group-segment-meta">
                      <small>第 {{ task.segment_index || 1 }} 段</small>
                      <small class="video-seg-ref" :class="{ warn: !task.ref }">{{ task.ref ? refText(task.ref) : '无参考图 · 会变脸' }}</small>
                      <p>{{ task.segment_title || task.prompt || '未命名' }}</p>
                    </div>
                    <div class="video-group-segment-actions"><button title="下载本段" @click="videoDownload(task)">↓</button><button title="删除本段" @click="removeVideo(task)">×</button></div>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="work-video"><video :src="assetUrl(group.list[0].url)" preload="metadata" muted playsinline></video><div><button @click="videoDownload(group.list[0])" title="下载">↓</button><button @click="removeVideo(group.list[0])" title="删除">×</button></div></div>
                <p>{{ group.list[0].prompt || '未命名创作' }}</p>
                <small>{{ group.list[0].createtime_text || group.list[0].createtime || '' }} · {{ (group.list[0].resolution || '').toUpperCase() }} · {{ group.list[0].duration }} 秒</small>
              </template>
            </article>
          </div>
          <div v-if="videoGalleryTotal > videoPageSize" class="pagination"><button :disabled="videoGalleryPage === 1" @click="changeVideoGalleryPage(-1)">‹</button><button v-for="n in videoTotalPages" :key="n" :class="{ active: videoGalleryPage === n }" @click="setVideoGalleryPage(n)">{{ n }}</button><button :disabled="videoGalleryPage === videoTotalPages" @click="changeVideoGalleryPage(1)">›</button></div>
        </template>
      </section>

      <section v-else class="content-section pricing-section">
        <div class="pricing-intro"><span class="eyebrow">灵活计费</span><h2>为你的创意补充算力</h2><p>一次充值，永久有效。根据创作需求选择合适的算力包。</p></div>
        <div v-if="plansLoading" class="loading-list"><span class="spinner"></span>正在载入套餐...</div>
        <div v-else-if="plans.length" class="plans">
          <article v-for="(plan, index) in plans" :key="plan.id || index" :class="{ featured: plan.recommended }">
            <span v-if="plan.recommended" class="recommend">推荐</span><small>{{ plan.name }}</small><div class="price"><span>¥</span><strong>{{ plan.price }}</strong></div><p>{{ plan.description || '算力永久有效，可用于后台配置模型' }}</p><div class="plan-credit"><b>{{ plan.credits }}</b><span>创作算力</span></div><button class="button" :class="plan.recommended ? 'primary' : 'ghost'" @click="recharge(plan)">选择方案</button>
          </article>
        </div>
        <div v-else class="plans-unavailable"><h3>套餐暂不可用</h3><p>价格方案加载失败，请稍后刷新重试。</p><button class="button ghost" @click="loadPlans">重新加载</button></div>
        <div class="custom-recharge">
          <div><span class="eyebrow">自定义充值</span><h3>按需补充算力</h3><p>最低充值 1 元，充值金额与到账算力等额。</p></div>
          <div class="custom-recharge-form"><label><span>¥</span><input v-model.trim="customAmount" type="text" min="1" max="99999999.99" step="0.01" inputmode="decimal" placeholder="输入充值金额" /></label><small>到账 {{ customCredit }} 算力</small><button class="button primary" @click="rechargeCustom">立即充值</button></div>
        </div>
        <div class="pricing-notes"><span>✓ 算力永久有效</span><span>✓ 支持后台配置模型</span><span>✓ 作品高清下载</span><span>✓ 支付安全保障</span></div>
      </section>
    </main>

    <div v-if="authVisible" class="modal-backdrop" @click.self="authVisible = false">
      <div class="modal auth-modal">
        <button class="modal-close" @click="authVisible = false">×</button><div class="modal-brand"><span class="brand-mark">A</span></div>
        <h2>{{ authMode === 'login' ? '欢迎回来' : '创建创作账户' }}</h2><p>{{ authMode === 'login' ? '登录以继续你的 AI 创作之旅' : '注册账户以开始 AI 创作' }}</p>
        <form @submit.prevent="submitAuth">
          <label><span>{{ authMode === 'login' ? '账号' : '用户名' }}</span><input v-model.trim="authForm.username" autocomplete="username" :placeholder="authMode === 'login' ? '输入用户名' : '设置用户名'" required /></label>
          <label><span>密码</span><input v-model="authForm.password" type="password" :autocomplete="authMode === 'login' ? 'current-password' : 'new-password'" minlength="6" placeholder="至少 6 位字符" required /></label>
          <button class="button primary auth-submit" :disabled="authLoading">{{ authLoading ? '请稍候...' : authMode === 'login' ? '登录' : '完成注册' }}</button>
        </form>
        <div class="auth-switch">{{ authMode === 'login' ? '还没有账号？' : '已有账号？' }}<button @click="switchAuth">{{ authMode === 'login' ? '立即注册' : '返回登录' }}</button></div>
      </div>
    </div>

    <div v-if="payVisible" class="modal-backdrop" @click.self="closePay">
      <div class="modal pay-modal"><button class="modal-close" @click="closePay">×</button><span class="eyebrow">扫码支付</span><h2>充值 {{ selectedPlan.name }}</h2><div class="pay-amount">¥ {{ selectedPlan.price }}</div><div class="qr-wrap"><img v-if="payOrder.qrcode" :src="assetUrl(payOrder.qrcode)" alt="充值二维码" /><div v-else class="qr-loading"><span class="spinner"></span></div></div><p>请使用微信扫码支付</p><small>订单号：{{ payOrder.order_no || '创建中...' }}</small><div class="pay-status"><i></i>{{ payStatus }}</div></div>
    </div>

    <div v-if="toast.text" class="toast" :class="toast.type">{{ toast.text }}</div>
  </div>
</template>

<script>
import { api, assetUrl } from './services/api'

const qualityDefaults = [
  { label: '1K', value: '1K', price: 1, description: '标准清晰度' },
  { label: '2K', value: '2K', price: 2, description: '高清细节' },
  { label: '4K', value: '4K', price: 4, description: '超清输出' }
]

const sizeMap = {
  '1K': { '1:1': '1024x1024', '16:9': '1024x576', '9:16': '576x1024' },
  '2K': { '1:1': '2048x2048', '16:9': '2048x1152', '9:16': '1152x2048' },
  '4K': { '1:1': '4096x4096', '16:9': '4096x2304', '9:16': '2304x4096' }
}

export default {
  name: 'App',
  data() {
    return {
      page: 'create', user: null, authVisible: false, authMode: 'login', authLoading: false,
      authForm: { username: '', password: '' },
      form: { mode: 'generate', prompt: '', quality: '1K', ratio: '1:1', videoDuration: 5, videoResolution: '720p' },
      modes: [{ label: '图片生成', value: 'generate' }, { label: '图片编辑', value: 'edit' }, { label: '视频生成', value: 'video' }],
      sourceImage: null, imagePreview: '',
      videoImage: null, videoImagePreview: '',
      videoResolutions: [{ value: '480p', label: '480P', price: 0.04 }, { value: '720p', label: '720P', price: 0.05 }, { value: '1080p', label: '1080P', price: 0.2 }],
      videoConfigLoaded: false, videoMinDuration: 1, videoMaxDuration: 15,
      videoInputMode: 'single', splitPrice: 0.01, maxScriptLength: 5000, mergeAvailable: false,
      scriptText: '', scenes: [], batchTasks: [], splitting: false, batchGenerating: false, batchStop: false, batchNo: '',
      characters: [], portraitBusyIndex: -1, portraitTargetIndex: -1, strictPortrait: false,
      qualities: qualityDefaults.map(item => ({ ...item })),
      priceMatrix: null,
      ratios: [{ label: '方形', value: '1:1', style: { width: '18px', height: '18px' } }, { label: '横向', value: '16:9', style: { width: '24px', height: '14px' } }, { label: '纵向', value: '9:16', style: { width: '14px', height: '24px' } }],
      promptExamples: [{ icon: '◈', title: '建筑空间', desc: '未来主义与光影', prompt: '极简未来主义建筑空间，巨大的弧形混凝土结构，柔和天光，静谧氛围，建筑摄影，高细节' }, { icon: '◎', title: '人物肖像', desc: '电影感人像', prompt: '电影感人物肖像，侧逆光，细腻皮肤质感，浅景深，35mm 胶片色彩，专业摄影' }, { icon: '⌁', title: '自然景观', desc: '壮阔与诗意', prompt: '清晨云海中的雪山，金色阳光穿透薄雾，史诗感构图，超广角风光摄影，真实细节' }],
      generating: false, generateError: '', results: [], works: [], galleryPage: 1, pageSize: 8, galleryTotal: 0, galleryLoading: false,
      videoTasks: [], videoTimer: null, videoPolling: false,
      galleryTab: 'image', videoGroups: [], videoGalleryPage: 1, videoPageSize: 8, videoGalleryTotal: 0, videoGalleryLoading: false,
      plans: [], plansLoading: false, customAmount: '', payVisible: false, selectedPlan: {}, payOrder: {}, payStatus: '等待支付', payTimer: null,
      toast: { text: '', type: 'success' }, toastTimer: null
    }
  },
  computed: {
    pageTitle() { return { create: '创作台', gallery: '作品相册', pricing: '价格与算力' }[this.page] },
    displayName() { return (this.user && (this.user.nickname || this.user.username)) || '创作者' },
    totalPages() { return Math.max(1, Math.ceil(this.galleryTotal / this.pageSize)) },
    selectedQuality() { return this.qualities.find(item => item.value === this.form.quality) || this.qualities[0] },
    totalCost() { return Number(this.selectedQuality.price || 0) },
    formattedTotalCost() { return this.formatCost(this.totalCost) },
    selectedVideoResolution() { return this.videoResolutions.find(item => item.value === this.form.videoResolution) || this.videoResolutions[0] || { value: '720p', label: '720P', price: 0 } },
    videoTotalCost() { return Number(this.selectedVideoResolution.price || 0) * Number(this.form.videoDuration || 0) },
    formattedVideoCost() { return this.formatCost(this.videoTotalCost) },
    sceneCost() { return this.videoTotalCost },
    batchTotalCost() { return this.videoTotalCost * this.scenes.length },
    formattedBatchCost() { return this.formatCost(this.batchTotalCost) },
    batchDoneCount() { return this.batchTasks.filter(task => task && task.status === 'success').length },
    batchRemaining() { return Math.max(0, this.scenes.length - this.batchDoneCount) },
    portraitPrice() {
      const matrix = this.priceMatrix
      if (matrix && matrix.generate && matrix.generate['1K'] !== undefined) return Number(matrix.generate['1K']) || 0
      return Number(this.selectedQuality && this.selectedQuality.price) || 0
    },
    hasCharacterPortrait() { return this.characters.some(role => !!role.portraitFile) },
    activeVideoTasks() {
      if (this.form.mode !== 'video') return []
      if (this.videoInputMode === 'batch') {
        const list = []
        this.batchTasks.forEach((task, index) => {
          if (task) list.push({ ...task, _index: index, _batch: true })
        })
        return list
      }
      return this.videoTasks
    },
    currentCostText() {
      if (this.form.mode !== 'video') return this.formattedTotalCost
      return this.videoInputMode === 'batch' ? this.formattedBatchCost : this.formattedVideoCost
    },
    batchButtonText() {
      if (this.batchGenerating) return '正在分段生成...'
      if (!this.scenes.length) return '请先智能拆分'
      const prefix = this.batchDoneCount ? `继续生成剩余 ${this.batchRemaining} 段` : `开始生成 ${this.scenes.length} 段`
      return `${prefix} · 最多 ${this.formattedBatchCost} 算力`
    },
    videoTotalPages() { return Math.max(1, Math.ceil(this.videoGalleryTotal / this.videoPageSize)) },
    generateButtonText() {
      if (this.generating) return this.form.mode === 'video' ? '正在生成视频...' : this.form.mode === 'edit' ? '正在编辑...' : '正在生成...'
      if (this.form.mode === 'video') return `立即生成视频 · ${this.formattedVideoCost} 算力`
      return `${this.form.mode === 'edit' ? '立即编辑' : '立即生成'} · ${this.formattedTotalCost} 算力`
    },
    customCredit() {
      const amount = Number(this.customAmount)
      return Number.isFinite(amount) && amount >= 1 ? amount.toFixed(2) : '0.00'
    }
  },
  created() {
    const saved = localStorage.getItem('ai_user')
    if (saved) { try { this.user = JSON.parse(saved) } catch (e) { localStorage.removeItem('ai_user') } }
    window.addEventListener('auth-expired', this.authExpired)
    window.addEventListener('focus', this.refreshProfile)
    this.loadConfig()
    this.refreshProfile()
  },
  beforeDestroy() { window.removeEventListener('auth-expired', this.authExpired); window.removeEventListener('focus', this.refreshProfile); clearInterval(this.payTimer); this.stopVideoPolling(); this.batchStop = true; if (this.imagePreview) URL.revokeObjectURL(this.imagePreview); if (this.videoImagePreview) URL.revokeObjectURL(this.videoImagePreview); this.releaseCharacterPortraits(this.characters) },
  methods: {
    assetUrl,
    go(page) {
      this.page = page
      if (page === 'create') { this.generateError = ''; if (this.form.mode === 'video') this.loadVideoConfig() }
      window.scrollTo({ top: 0, behavior: 'smooth' })
      if (page === 'gallery') { if (this.galleryTab === 'video') this.loadVideoGallery(); else this.loadGallery() }
      if (page === 'pricing') this.loadPlans()
    },
    openAuth(mode) { this.authMode = mode; this.authVisible = true },
    switchAuth() { this.authMode = this.authMode === 'login' ? 'register' : 'login' },
    notify(text, type = 'success') { clearTimeout(this.toastTimer); this.toast = { text, type }; this.toastTimer = setTimeout(() => { this.toast.text = '' }, 3000) },
    authExpired() { this.user = null; localStorage.removeItem('ai_token'); localStorage.removeItem('ai_user'); this.openAuth('login'); this.notify('登录已过期，请重新登录', 'error') },
    async refreshProfile() {
      if (!this.user || !localStorage.getItem('ai_token')) return
      try {
        const data = await api.profile()
        if (data.userinfo) this.updateUser(data.userinfo)
        else if (data.money !== undefined) this.updateUser({ money: data.money })
      } catch (e) { /* 鉴权失效由请求层统一处理 */ }
    },
    async submitAuth() {
      this.authLoading = true
      try {
        const data = this.authMode === 'login' ? await api.login({ account: this.authForm.username, password: this.authForm.password }) : await api.register(this.authForm)
        this.user = data.userinfo
        localStorage.setItem('ai_token', data.userinfo.token)
        localStorage.setItem('ai_user', JSON.stringify(data.userinfo))
        this.authVisible = false
        this.notify(this.authMode === 'login' ? '登录成功' : '注册成功，欢迎加入')
      } catch (e) { this.notify(e.message, 'error') } finally { this.authLoading = false }
    },
    async logout() { try { await api.logout() } catch (e) { /* local logout remains valid */ } this.user = null; localStorage.removeItem('ai_token'); localStorage.removeItem('ai_user'); this.notify('已退出登录') },
    async loadConfig() {
      try {
        const data = await api.config()
        const prices = data.prices || data.quality_prices || data.resolution_prices
        if (prices && (prices.generate || prices.edit)) {
          this.priceMatrix = prices
          this.applyModePrices()
        } else if (prices) this.qualities = this.qualities.map(item => ({ ...item, price: Number(prices[item.value] ?? prices[item.value.toLowerCase()] ?? item.price) }))
        else if (data.cost !== undefined) this.qualities = this.qualities.map((item, index) => ({ ...item, price: Number(data.cost) * Math.pow(2, index) }))
      } catch (e) { /* defaults keep the editor usable */ }
    },
    async loadVideoConfig() {
      if (this.videoConfigLoaded) return
      this.videoConfigLoaded = true
      try {
        const data = await api.videoConfig()
        if (data && Array.isArray(data.resolutions) && data.resolutions.length) {
          this.videoResolutions = data.resolutions.map(item => ({ value: item.value, label: item.label || String(item.value).toUpperCase(), price: Number(item.price ?? (data.prices && data.prices[item.value]) ?? 0) }))
          if (!this.videoResolutions.some(item => item.value === this.form.videoResolution)) this.form.videoResolution = this.videoResolutions[0].value
        } else if (data && data.prices) this.videoResolutions = this.videoResolutions.map(item => ({ ...item, price: Number(data.prices[item.value] ?? item.price) }))
        if (data && data.min_duration) this.videoMinDuration = Number(data.min_duration)
        if (data && data.max_duration) this.videoMaxDuration = Number(data.max_duration)
        if (data && data.default_duration) this.form.videoDuration = Math.min(this.videoMaxDuration, Math.max(this.videoMinDuration, Number(data.default_duration)))
        if (data && data.split_price !== undefined) this.splitPrice = Number(data.split_price)
        if (data && data.max_script_length) this.maxScriptLength = Number(data.max_script_length)
        if (data && data.merge_available !== undefined) this.mergeAvailable = !!data.merge_available
      } catch (e) { this.videoConfigLoaded = false }
    },
    setMode(mode) {
      if (this.form.mode === mode) return
      this.form.mode = mode
      this.generateError = ''
      if (mode === 'video') {
        this.loadVideoConfig()
        if (this.videoTasks.some(task => task.status === 'processing')) this.startVideoPolling()
      } else {
        this.stopVideoPolling()
        this.results = []
      }
      this.applyModePrices()
    },
    applyModePrices() {
      if (!this.priceMatrix || !this.priceMatrix[this.form.mode]) return
      const prices = this.priceMatrix[this.form.mode]
      this.qualities = this.qualities.map(item => ({ ...item, price: Number(prices[item.value] ?? prices[item.value.toLowerCase()] ?? item.price) }))
    },
    setVideoInputMode(mode) {
      if (this.videoInputMode === mode) return
      if (this.batchGenerating) return this.notify('正在分段生成，请先停止后再切换', 'error')
      this.videoInputMode = mode
      this.generateError = ''
      if (mode === 'single') {
        if (this.videoTasks.some(task => task.status === 'processing')) this.startVideoPolling()
      } else {
        this.stopVideoPolling()
      }
    },
    sleep(ms) { return new Promise(resolve => setTimeout(resolve, ms)) },
    async splitScript() {
      if (!this.user) return this.openAuth('login')
      this.generateError = ''
      if (this.scriptText.length < 10) return this.notify('请粘贴更完整的视频文案', 'error')
      await this.refreshProfile()
      if (this.splitPrice > 0 && Number(this.user.money || 0) < this.splitPrice) {
        this.generateError = `算力余额不足，本次智能拆分需要 ${this.formatCost(this.splitPrice)} 算力`
        return this.notify(this.generateError, 'error')
      }
      this.splitting = true
      try {
        const data = await api.videoSplit({ script: this.scriptText })
        const list = (data.list || []).filter(item => item && item.prompt)
        if (!list.length) throw new Error('文案拆分失败，请调整文案后重试')
        this.scenes = list.map((item, index) => ({
          title: item.title || `分镜 ${index + 1}`,
          cast: Array.isArray(item.cast) ? item.cast.filter(name => !!name) : [],
          scene: item.scene || '',
          transition: index === 0 || item.transition === 'cut' ? 'cut' : 'continue',
          prompt: item.prompt
        }))
        const roles = (data.characters || [])
          .map(item => ({
            name: (item && item.name) || '',
            desc: (item && (item.desc || item.description)) || '',
            portraitFile: null,
            portraitPreview: ''
          }))
          .filter(item => item.name || item.desc)
        if (!roles.length && data.character) {
          roles.push({ name: '主角', desc: data.character, portraitFile: null, portraitPreview: '' })
        }
        // 重新拆分时尽量继承已生成的定妆照：优先按角色名匹配，模型改名时按顺序匹配
        const previous = this.characters
        const reused = []
        roles.forEach((role, index) => {
          let old = previous.find(item => item.name && item.name === role.name && item.portraitFile && reused.indexOf(item) === -1)
          if (!old && previous.length === roles.length) {
            const candidate = previous[index]
            if (candidate && candidate.portraitFile && reused.indexOf(candidate) === -1) {
              old = candidate
            }
          }
          if (old) {
            reused.push(old)
            role.portraitFile = old.portraitFile
            role.portraitPreview = old.portraitPreview
          }
        })
        const orphan = previous.filter(item => item.portraitFile && reused.indexOf(item) === -1)
        this.releaseCharacterPortraits(orphan)
        this.characters = roles
        if (orphan.length) {
          this.notify(`有 ${orphan.length} 张已生成的定妆照没能对应到新角色，请重新设置`, 'error')
        }
        this.batchTasks = this.scenes.map(() => null)
        this.batchNo = ''
        if (data.money !== undefined) this.updateUser({ money: data.money })
        this.notify(`已拆分为 ${this.scenes.length} 段，可编辑后逐段生成`)
      } catch (e) {
        this.generateError = e.message || '拆分失败，请稍后重试'
        this.notify(this.generateError, 'error')
        await this.refreshProfile()
      } finally { this.splitting = false }
    },
    clearScript() {
      if (this.batchGenerating) return
      this.releaseCharacterPortraits(this.characters)
      this.scriptText = ''
      this.scenes = []
      this.batchTasks = []
      this.batchNo = ''
      this.characters = []
      this.generateError = ''
    },
    releaseCharacterPortraits(list) {
      (list || []).forEach(role => {
        if (role && role.portraitPreview) URL.revokeObjectURL(role.portraitPreview)
      })
    },
    addCharacter() {
      if (this.batchGenerating) return
      this.characters.push({ name: this.characters.length ? '' : '主角', desc: '', portraitFile: null, portraitPreview: '' })
    },
    removeCharacter(index) {
      if (this.batchGenerating || this.characters.length <= 1) return
      const role = this.characters[index]
      if (role && role.portraitPreview) URL.revokeObjectURL(role.portraitPreview)
      this.characters.splice(index, 1)
    },
    sceneCastText(scene) {
      const cast = (scene && Array.isArray(scene.cast) ? scene.cast : []).filter(name => !!name)
      return cast.length ? cast.join('、') : (this.characters[0] && this.characters[0].name) || '主角'
    },
    refText(ref) {
      if (ref === 'prev') return '起手：上一段最后一帧'
      if (ref === 'upload') return '起手：定妆照'
      return ''
    },
    sceneCharacters(scene) {
      const cast = (scene && Array.isArray(scene.cast) ? scene.cast : []).filter(name => !!name)
      if (!cast.length) return this.characters
      const matched = this.characters.filter(role => role.name && cast.indexOf(role.name) !== -1)
      return matched.length ? matched : this.characters
    },
    /**
     * 把用户手动上传的照片压成和「AI 定妆照」完全同规格的图（1K JPEG、铺白底去掉透明通道）。
     * 手机照片动辄 4000px 以上、可能带透明通道或特殊色彩格式，上游会直接忽略这种参考图，
     * 导致视频按纯文字生成、角色长相完全不认。统一再编码后就不会有这个问题。
     */
    async normalizeImageFile(file, maxSize = 1024) {
      const url = URL.createObjectURL(file)
      try {
        const image = await new Promise((resolve, reject) => {
          const img = new Image()
          img.onload = () => resolve(img)
          img.onerror = () => reject(new Error('图片解析失败，请换一张图片试试'))
          img.src = url
        })
        const width = image.naturalWidth || image.width
        const height = image.naturalHeight || image.height
        if (!width || !height) throw new Error('图片解析失败，请换一张图片试试')
        const scale = Math.min(1, maxSize / Math.max(width, height))
        const targetWidth = Math.max(1, Math.round(width * scale))
        const targetHeight = Math.max(1, Math.round(height * scale))
        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) return file
        // 先铺白底：PNG 透明通道会让上游把图转首帧时出错
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, targetWidth, targetHeight)
        ctx.drawImage(image, 0, 0, targetWidth, targetHeight)
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92))
        if (!blob) return file
        return new File([blob], 'portrait.jpg', { type: 'image/jpeg' })
      } finally {
        URL.revokeObjectURL(url)
      }
    },
    pickCharacterPortrait(index) {
      if (this.batchGenerating || this.portraitBusyIndex !== -1) return
      if (!this.characters[index]) return
      this.portraitTargetIndex = index
      const input = this.$refs.characterPortraitInput
      if (input) {
        input.value = ''
        input.click()
      }
    },
    async selectCharacterPortrait(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      const index = this.portraitTargetIndex
      this.portraitTargetIndex = -1
      if (!file || index < 0) return
      if (file.size > 10485760) return this.notify('图片不能超过 10MB', 'error')
      if (file.type && file.type.indexOf('image/') !== 0) return this.notify('请选择图片文件', 'error')
      this.portraitBusyIndex = index
      try {
        this.setCharacterPortrait(index, await this.normalizeImageFile(file))
      } catch (e) {
        this.notify(e.message || '图片处理失败，请换一张图片试试', 'error')
      } finally {
        this.portraitBusyIndex = -1
      }
    },
    setCharacterPortrait(index, file) {
      const role = this.characters[index]
      if (!role) return
      if (role.portraitPreview) URL.revokeObjectURL(role.portraitPreview)
      this.$set(role, 'portraitFile', file)
      this.$set(role, 'portraitPreview', URL.createObjectURL(file))
    },
    clearCharacterPortrait(index) {
      const role = this.characters[index]
      if (!role) return
      if (role.portraitPreview) URL.revokeObjectURL(role.portraitPreview)
      this.$set(role, 'portraitFile', null)
      this.$set(role, 'portraitPreview', '')
    },
    segmentPrompt(scene, index, startType) {
      const parts = []
      // 参考图约束放最前面，模型对开头指令的权重更高
      if (startType === 'portrait') {
        parts.push('【参考图】第 1 帧就是参考图，片中人物必须与参考图是同一个人：脸型、五官、发型、体型胖瘦与服装必须完全一致，不得改变身材、不得换人。')
      } else if (startType === 'prev') {
        parts.push('【参考图】第 1 帧就是参考图（上一段最后一帧），片中人物必须与它完全一致：脸型、五官、发型、体型胖瘦与服装不得改变。')
      }
      const roles = this.sceneCharacters(scene)
      const described = roles.filter(role => (role.desc || '').trim())
      if (described.length) {
        parts.push(`【角色】${described.map(role => `${role.name || '角色'}：${role.desc.trim()}`).join('；')}。外貌、发型、体型胖瘦与服装以参考图为准，文字描述仅用于区分角色。`)
      } else if (roles.length) {
        // 只上传了定妆照、没写外貌描述时，也要明确告诉模型人物以参考图为准
        parts.push(`【角色】${roles.map(role => role.name || '角色').join('、')}。人物外貌完全以参考图为准，不得自行设计脸型、发型与体型。`)
      }
      const env = ((scene && scene.scene) || '').trim()
      if (env) parts.push(`【环境】${env}`)
      parts.push(`【画面】${(scene && scene.prompt) || ''}`)
      if (index > 0) {
        if (startType === 'prev') {
          parts.push('【衔接】从参考图的人物位置、姿态与动作方向自然延续，不要重置姿势、不要跳切镜头，所有角色外貌、服装与环境保持不变')
        } else if (scene && scene.transition === 'cut') {
          parts.push('【衔接】本段为新场景，参考图是该段出场角色的定妆照，所有角色外貌必须与定妆照完全一致')
        } else {
          parts.push('【衔接】延续上一段剧情，所有角色外貌、服装与所在环境保持不变，动作自然推进')
        }
      }
      return parts.join('\n').slice(0, 2000)
    },
    /**
     * 决定本段用哪张图起手：承接段用上一段最后一帧，其余用该段出场角色的定妆照
     */
    resolveStart(index, scene) {
      const roles = this.sceneCharacters(scene)
      const portrait = roles.find(role => role.portraitFile)
      const prev = index > 0 ? this.batchTasks[index - 1] : null
      const canChain = index > 0 && !(scene && scene.transition === 'cut') && !!(prev && prev.id && prev.status === 'success')
      if (this.strictPortrait) {
        if (portrait) return { type: 'portrait', role: portrait.name, file: portrait.portraitFile, preview: portrait.portraitPreview }
        if (canChain) return { type: 'prev', role: '', file: null, preview: '' }
        return { type: 'none', role: '', file: null, preview: '' }
      }
      if (canChain) return { type: 'prev', role: '', file: null, preview: '' }
      if (portrait) return { type: 'portrait', role: portrait.name, file: portrait.portraitFile, preview: portrait.portraitPreview }
      return { type: 'none', role: '', file: null, preview: '' }
    },
    /**
     * 找出没有任何参考图起手的段号：这些段的角色长相不会被定妆照约束
     * 承接段的参考图来自上一段，所以按「上一段会有产出」推算，而不是看当前完成状态
     */
    missingStartSegments() {
      const list = []
      let chainable = false
      this.scenes.forEach((scene, index) => {
        const portrait = this.sceneCharacters(scene).find(role => role.portraitFile)
        const isChainScene = index > 0 && !(scene && scene.transition === 'cut')
        const willChain = !this.strictPortrait && isChainScene && chainable
        if (!portrait && !willChain) {
          list.push(index + 1)
        }
        chainable = willChain || !!portrait
      })
      return list
    },
    startThumb(index, scene) {
      const start = this.resolveStart(index, scene)
      return start.preview || ''
    },
    startHint(index, scene) {
      const start = this.resolveStart(index, scene)
      if (start.type === 'prev') return '起手：上一段最后一帧'
      if (start.type === 'portrait') return `起手：${start.role || '角色'}的定妆照`
      if (this.strictPortrait) return '该段角色没有定妆照，强约束未生效'
      return '没有可用参考图，角色长相会漂移'
    },
    async generateCharacterPortrait(index) {
      if (!this.user) return this.openAuth('login')
      if (this.batchGenerating || this.portraitBusyIndex !== -1) return
      const role = this.characters[index]
      if (!role) return
      const desc = (role.desc || '').trim()
      if (!desc) return this.notify('请先填写该角色的外貌设定', 'error')
      await this.refreshProfile()
      const cost = this.portraitPrice
      if (cost > 0 && Number(this.user.money || 0) < cost) {
        return this.notify(`算力余额不足，生成定妆照需要 ${this.formatCost(cost)} 算力`, 'error')
      }
      const name = (role.name || '').trim()
      this.portraitBusyIndex = index
      this.generateError = ''
      try {
        const data = await api.generate({
          prompt: `角色定妆照：${name ? name + '，' : ''}${desc}。正面半身像，纯色简洁背景，均匀柔光，高清写实，五官清晰，服装完整，表情中性`,
          ratio: '1:1',
          resolution: '1K'
        })
        const image = (data.images && data.images[0]) || (data.list && data.list[0])
        if (!image || (!image.id && !image.url)) throw new Error('定妆照生成失败，请稍后重试')
        if (data.userinfo) this.updateUser(data.userinfo)
        else if (data.money !== undefined) this.updateUser({ money: data.money })
        // 静态 uploads 目录是跨域资源、没有 CORS 头，直接 fetch 会被浏览器拦截，
        // 所以走下载接口取原图字节再转成本地文件
        const source = image.id ? api.downloadUrl(image.id) : this.assetUrl(image.url)
        const response = await fetch(source)
        if (!response.ok) throw new Error('定妆照下载失败，请稍后重试')
        const blob = await response.blob()
        const type = blob.type && blob.type.indexOf('image/') === 0 ? blob.type : 'image/jpeg'
        this.setCharacterPortrait(index, new File([blob], 'portrait.jpg', { type: type }))
        this.notify(`「${name || '该角色'}」的定妆照已生成`)
      } catch (e) {
        this.notify(e.message || '定妆照生成失败', 'error')
        await this.refreshProfile()
      } finally {
        this.portraitBusyIndex = -1
      }
    },
    removeScene(index) {
      if (this.batchGenerating) return
      this.scenes.splice(index, 1)
      this.batchTasks.splice(index, 1)
    },
    stopBatch() {
      this.batchStop = true
      this.notify('已停止，当前这一段完成生成后不再继续')
    },
    async startBatch() {
      if (!this.user) return this.openAuth('login')
      if (!this.scenes.length) return this.notify('请先智能拆分文案', 'error')
      await this.refreshProfile()
      if (Number(this.user.money || 0) < this.videoTotalCost) {
        this.generateError = `算力余额不足，当前可用 ${this.formatCost(this.user.money || 0)} 算力，每段需要 ${this.formattedVideoCost} 算力`
        return this.notify(this.generateError, 'error')
      }
      if (!this.batchNo) this.batchNo = 'B' + Date.now().toString(36).toUpperCase()
      const missing = this.missingStartSegments()
      if (missing.length && !window.confirm(`第 ${missing.join('、')} 段没有任何参考图，生成出来的角色长相会和定妆照不一样。\n建议先给这些段出场的角色设置定妆照，或把这些段设为「承接上一段」。\n是否仍然继续生成？`)) {
        return
      }
      this.batchGenerating = true
      this.batchStop = false
      try {
        for (let index = 0; index < this.scenes.length; index++) {
          if (this.batchStop) break
          const existing = this.batchTasks[index]
          if (existing && existing.status === 'success') continue
          if (existing && existing.status === 'processing') { await this.pollTaskUntilDone(existing); continue }
          await this.runBatchSegment(index)
        }
      } finally {
        this.batchGenerating = false
        this.batchStop = false
      }
      const failed = this.batchTasks.filter(task => task && task.status === 'failed').length
      if (failed) this.notify(`已完成 ${this.batchDoneCount} 段，${failed} 段失败，可单独重试`, 'error')
      else if (this.batchRemaining === 0) this.notify('全部片段生成完成，可打包下载')
    },
    async retryBatchSegment(index) {
      if (!this.user) return this.openAuth('login')
      if (this.batchGenerating) return
      await this.refreshProfile()
      if (Number(this.user.money || 0) < this.videoTotalCost) {
        return this.notify(`算力余额不足，本段需要 ${this.formattedVideoCost} 算力`, 'error')
      }
      if (!this.batchNo) this.batchNo = 'B' + Date.now().toString(36).toUpperCase()
      this.batchGenerating = true
      this.batchStop = false
      try { await this.runBatchSegment(index) } finally {
        this.batchGenerating = false
        this.batchStop = false
      }
    },
    async runBatchSegment(index) {
      const scene = this.scenes[index]
      const start = this.resolveStart(index, scene)
      const prompt = this.segmentPrompt(scene, index, start.type)
      const payload = {
        prompt,
        duration: this.form.videoDuration,
        resolution: this.form.videoResolution,
        batch_no: this.batchNo,
        segment_index: index + 1,
        segment_title: scene.title || ''
      }
      // 承接段用上一段最后一帧起手，新场景（含第 1 段）用该段出场角色的定妆照起手
      let image = null
      const prev = index > 0 ? this.batchTasks[index - 1] : null
      if (start.type === 'prev' && prev && prev.id) {
        payload.prev_id = prev.id
      } else if (start.type === 'portrait') {
        image = start.file
      }
      let data
      try {
        data = await api.videoSubmit(payload, image)
      } catch (e) {
        this.$set(this.batchTasks, index, {
          id: 0, status: 'failed', progress: 0, error: e.message || '提交失败',
          title: scene.title, prompt: prompt, duration: this.form.videoDuration,
          resolution: this.form.videoResolution, cost: this.formattedVideoCost, segment_index: index + 1
        })
        return
      }
      const task = {
        id: data.id, task_no: data.task_no, status: 'processing', progress: 0,
        title: scene.title, prompt: prompt, ref: data.ref || '', source_image: data.source_image || '',
        duration: data.duration || this.form.videoDuration,
        resolution: data.resolution || this.form.videoResolution,
        cost: data.cost || this.formattedVideoCost, segment_index: index + 1, polls: 0
      }
      this.$set(this.batchTasks, index, task)
      await this.pollTaskUntilDone(task)
    },
    async pollTaskUntilDone(task) {
      while (task.status === 'processing' && !this.batchStop) {
        await this.sleep(3000)
        if (task.status !== 'processing' || this.batchStop) break
        task.polls = (task.polls || 0) + 1
        if (task.polls > 300) { task.status = 'failed'; task.error = '生成超时，请重试'; break }
        try {
          const data = await api.videoStatus(task.id)
          task.progress = Number(data.progress) || task.progress
          task.status = data.status
          if (data.status === 'success') {
            task.url = data.url
            if (data.cost) task.cost = data.cost
            if (data.money !== undefined) this.updateUser({ money: data.money })
          } else if (data.status === 'failed') {
            task.error = data.error || '视频生成失败'
          }
        } catch (e) { /* 轮询期间的临时失败继续重试 */ }
      }
    },
    batchDownloadIds() {
      return this.batchTasks.filter(task => task && task.status === 'success' && task.id).map(task => task.id)
    },
    saveLink(url) {
      const link = document.createElement('a')
      link.href = url
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      link.remove()
    },
    downloadBatchZip() {
      const ids = this.batchDownloadIds()
      if (!ids.length) return this.notify('还没有可打包的片段', 'error')
      this.saveLink(api.videoZipUrl(ids))
    },
    downloadBatchMerge() {
      if (!this.mergeAvailable) return this.notify('服务器未安装 ffmpeg，暂不支持拼接', 'error')
      const ids = this.batchDownloadIds()
      if (ids.length < 2) return this.notify('至少需要两段才能拼接成一个视频', 'error')
      this.notify('正在拼接为一个视频，请稍候...')
      this.saveLink(api.videoMergeUrl(ids))
    },
    selectImage(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024) {
        event.target.value = ''
        return this.notify('请选择 10MB 以内的 JPG、PNG 或 WEBP 图片', 'error')
      }
      if (this.imagePreview) URL.revokeObjectURL(this.imagePreview)
      this.sourceImage = file
      this.imagePreview = URL.createObjectURL(file)
    },
    async selectVideoImage(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      if (file.size > 10 * 1024 * 1024) return this.notify('图片不能超过 10MB', 'error')
      if (file.type && file.type.indexOf('image/') !== 0) return this.notify('请选择图片文件', 'error')
      try {
        // 与定妆照同样处理：过大的照片会被上游忽略，导致参考图不生效
        const normalized = await this.normalizeImageFile(file, 1280)
        if (this.videoImagePreview) URL.revokeObjectURL(this.videoImagePreview)
        this.videoImage = normalized
        this.videoImagePreview = URL.createObjectURL(normalized)
      } catch (e) {
        this.notify(e.message || '图片处理失败，请换一张图片试试', 'error')
      }
    },
    clearVideoImage() {
      if (this.videoImagePreview) URL.revokeObjectURL(this.videoImagePreview)
      this.videoImage = null
      this.videoImagePreview = ''
      if (this.$refs.videoImageInput) this.$refs.videoImageInput.value = ''
    },
    formatCost(value) { return Number(value).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1') },
    selectedSize(ratio = this.form.ratio) { return sizeMap[this.form.quality][ratio].replace('x', ' × ') },
    randomPrompt() { const item = this.promptExamples[Math.floor(Math.random() * this.promptExamples.length)]; this.form.prompt = item.prompt },
    usePrompt(prompt) { this.form.prompt = prompt },
    async generate() {
      if (!this.user) return this.openAuth('login')
      this.generateError = ''
      if (this.form.prompt.length < 3) return this.notify('请先输入更完整的创意描述', 'error')
      if (this.form.mode === 'edit' && !this.sourceImage) return this.notify('请先上传待编辑图片', 'error')
      await this.refreshProfile()
      if (Number(this.user.money || 0) < this.totalCost) {
        this.generateError = `算力余额不足，当前可用 ${this.formatCost(this.user.money || 0)} 算力，本次需要 ${this.formattedTotalCost} 算力`
        return this.notify(this.generateError, 'error')
      }
      this.generating = true
      const payload = { prompt: this.form.prompt, resolution: this.form.quality, ratio: this.form.ratio }
      try {
        const data = this.form.mode === 'edit' ? await api.edit(payload, this.sourceImage) : await api.generate(payload)
        this.results = Array.isArray(data) ? data : (data.images || data.list || [])
        if (data.userinfo) this.updateUser(data.userinfo)
        else if (data.money !== undefined) this.updateUser({ money: data.money })
        this.notify(this.form.mode === 'edit' ? '图片编辑完成' : '作品生成完成')
      } catch (e) {
        this.generateError = e.message || '生成失败，请稍后重试'
        this.notify(this.generateError, 'error')
        await this.refreshProfile()
      } finally { this.generating = false }
    },
    async generateVideo() {
      if (!this.user) return this.openAuth('login')
      this.generateError = ''
      if (this.form.prompt.length < 3) return this.notify('请先输入更完整的创意描述', 'error')
      await this.refreshProfile()
      if (Number(this.user.money || 0) < this.videoTotalCost) {
        this.generateError = `算力余额不足，当前可用 ${this.formatCost(this.user.money || 0)} 算力，本次需要 ${this.formattedVideoCost} 算力`
        return this.notify(this.generateError, 'error')
      }
      this.generating = true
      try {
        const data = await api.videoSubmit({ prompt: this.form.prompt, duration: this.form.videoDuration, resolution: this.form.videoResolution }, this.videoImage)
        const task = {
          id: data.id, task_no: data.task_no, status: 'processing', progress: 0,
          prompt: this.form.prompt, duration: data.duration || this.form.videoDuration,
          resolution: data.resolution || this.form.videoResolution, cost: data.cost || this.formattedVideoCost,
          createtime_text: '刚刚', polls: 0
        }
        this.videoTasks.unshift(task)
        this.startVideoPolling()
        this.notify('视频任务已提交，正在生成')
      } catch (e) {
        this.generateError = e.message || '生成失败，请稍后重试'
        this.notify(this.generateError, 'error')
        await this.refreshProfile()
      } finally { this.generating = false }
    },
    startVideoPolling() {
      if (this.videoTimer) return
      this.videoTimer = setInterval(this.pollVideoTasks, 3000)
    },
    stopVideoPolling() { clearInterval(this.videoTimer); this.videoTimer = null },
    async pollVideoTasks() {
      if (this.videoPolling) return
      const pending = this.videoTasks.filter(task => task.status === 'processing')
      if (!pending.length) return this.stopVideoPolling()
      this.videoPolling = true
      try {
        await Promise.all(pending.map(async task => {
          if ((task.polls || 0) >= 200) { task.status = 'failed'; task.error = task.error || '生成超时，请稍后重试'; return }
          task.polls = (task.polls || 0) + 1
          try {
            const data = await api.videoStatus(task.id)
            Object.assign(task, data)
            if (data.status === 'success') {
              if (data.money !== undefined) this.updateUser({ money: data.money })
              this.notify('视频生成完成')
            } else if (data.status === 'failed') {
              this.notify(data.error || '视频生成失败', 'error')
            }
          } catch (e) { /* 轮询期间的临时失败继续重试 */ }
        }))
      } finally { this.videoPolling = false }
      if (!this.videoTasks.some(task => task.status === 'processing')) this.stopVideoPolling()
    },
    videoStatusText(status) { return status === 'success' ? '已完成' : status === 'failed' ? '生成失败' : '生成中' },
    videoDownload(task) {
      const link = document.createElement('a')
      link.href = api.videoDownloadUrl(task.id)
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      link.remove()
    },
    openVideo(task) { window.open(assetUrl(task.url), '_blank', 'noopener') },
    startCreateVideo() { this.setMode('video'); this.go('create') },
    updateUser(user) { this.user = { ...this.user, ...user }; localStorage.setItem('ai_user', JSON.stringify(this.user)) },
    preview(item) { window.open(assetUrl(item.url || item.image), '_blank', 'noopener') },
    download(item) {
      const link = document.createElement('a')
      link.href = api.downloadUrl(item.id)
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      link.remove()
    },
    async loadGallery() {
      if (!this.user) return this.openAuth('login')
      this.galleryLoading = true
      try { const data = await api.gallery({ page: this.galleryPage, limit: this.pageSize }); this.works = data.list || data.rows || []; this.galleryTotal = Number(data.total || this.works.length) } catch (e) { this.notify(e.message, 'error') } finally { this.galleryLoading = false }
    },
    setGalleryPage(page) { this.galleryPage = page; this.loadGallery() },
    changeGalleryPage(offset) { this.setGalleryPage(this.galleryPage + offset) },
    async removeWork(work) { if (!window.confirm('确定删除这张作品吗？')) return; try { await api.removeWork(work.id); this.notify('作品已删除'); this.loadGallery() } catch (e) { this.notify(e.message, 'error') } },
    switchGalleryTab(tab) {
      if (this.galleryTab === tab) return
      this.galleryTab = tab
      if (tab === 'image') this.loadGallery()
      else this.loadVideoGallery()
    },
    async loadVideoGallery() {
      if (!this.user) return this.openAuth('login')
      this.videoGalleryLoading = true
      // 拼接下载需要知道服务器是否装了 ffmpeg，顺便把配置拉一次（内部有缓存）
      this.loadVideoConfig()
      try {
        const data = await api.videoHistory({ page: this.videoGalleryPage, limit: this.videoPageSize })
        this.videoGroups = (data.groups || []).filter(group => group && group.list && group.list.length)
        this.videoGalleryTotal = Number(data.total || this.videoGroups.length)
      } catch (e) { this.notify(e.message, 'error') } finally { this.videoGalleryLoading = false }
    },
    isBatchGroup(group) { return !!(group && (group.segments > 1 || group.batch_no)) },
    downloadGroupMerge(group) {
      if (!this.mergeAvailable) return this.notify('服务器未安装 ffmpeg，暂不支持拼接', 'error')
      if (!group.ids || group.ids.length < 2) return this.notify('至少需要两段才能拼接成一个视频', 'error')
      this.notify('正在拼接为一个视频，请稍候...')
      this.saveLink(api.videoMergeUrl(group.ids))
    },
    downloadGroupZip(group) {
      if (!group.ids || !group.ids.length) return this.notify('没有可打包的片段', 'error')
      this.saveLink(api.videoZipUrl(group.ids))
    },
    setVideoGalleryPage(page) { this.videoGalleryPage = page; this.loadVideoGallery() },
    changeVideoGalleryPage(offset) { this.setVideoGalleryPage(this.videoGalleryPage + offset) },
    async removeVideo(video) {
      if (!window.confirm('确定删除这个视频吗？')) return
      try { await api.removeVideo(video.id); this.notify('视频已删除'); this.loadVideoGallery() } catch (e) { this.notify(e.message, 'error') }
    },
    async loadPlans() {
      this.plansLoading = true
      this.plans = []
      try {
        const data = await api.plans()
        const list = Array.isArray(data) ? data : (data.list || data.rows || [])
        this.plans = list.filter(plan => plan && plan.id).map(plan => ({ ...plan, price: plan.price ?? plan.amount, credits: plan.credits ?? plan.credit }))
      } catch (e) { this.notify(e.message, 'error') } finally { this.plansLoading = false }
    },
    async recharge(plan) {
      if (!this.user) return this.openAuth('login')
      this.selectedPlan = plan; this.payOrder = {}; this.payStatus = '正在创建订单'; this.payVisible = true
      try { this.payOrder = await api.createRecharge({ plan_id: plan.id, pay_type: 'qrcode' }); this.payStatus = '等待支付'; if (this.payOrder.order_no) this.startPayPolling() } catch (e) { this.payVisible = false; this.notify(e.message, 'error') }
    },
    async rechargeCustom() {
      if (!this.user) return this.openAuth('login')
      if (!/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(this.customAmount) || Number(this.customAmount) < 1) return this.notify('充值金额最低1元，最多保留两位小数', 'error')
      const amount = Number(this.customAmount).toFixed(2)
      this.selectedPlan = { name: '自定义充值', price: amount, credits: amount }; this.payOrder = {}; this.payStatus = '正在创建订单'; this.payVisible = true
      try { this.payOrder = await api.createRecharge({ amount, pay_type: 'qrcode' }); this.payStatus = '等待支付'; if (this.payOrder.order_no) this.startPayPolling() } catch (e) { this.payVisible = false; this.notify(e.message, 'error') }
    },
    startPayPolling() { clearInterval(this.payTimer); this.payTimer = setInterval(async () => { try { const data = await api.rechargeStatus(this.payOrder.order_no); if (data.paid || data.status === 'paid') { this.payStatus = '支付成功'; clearInterval(this.payTimer); if (data.userinfo) this.updateUser(data.userinfo); this.notify('充值成功'); setTimeout(this.closePay, 1200) } } catch (e) { /* continue polling transient failures */ } }, 2500) },
    closePay() { clearInterval(this.payTimer); this.payVisible = false }
  }
}
</script>

<style src="./assets/app.css"></style>
